import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  resolveLocalized,
  type ReportSection,
  type ReportStructureDTO,
  type LocalizedText,
} from "@arna-med/shared";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Plus, Trash2, Pencil, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import {
  useReportStructures,
  useSaveReportStructure,
  useDeleteReportStructure,
} from "@/hooks/api/useReportStructures";

// Same three authoring languages the template editor offers.
const LANGS: { code: string; label: string }[] = [
  { code: "ru", label: "RU" },
  { code: "kz", label: "KZ" },
  { code: "en", label: "EN" },
];

function langValue(v: LocalizedText | undefined | null, lang: string): string {
  if (v == null) return "";
  if (typeof v === "string") return lang === "ru" ? v : "";
  const val = v[lang];
  return typeof val === "string" ? val : "";
}

function setLang(
  current: LocalizedText | undefined | null,
  lang: string,
  text: string,
): Record<string, string> {
  const base: Record<string, string> =
    typeof current === "string"
      ? current
        ? { ru: current }
        : {}
      : { ...((current ?? {}) as Record<string, string>) };
  if (text) base[lang] = text;
  else delete base[lang];
  return base;
}

/** True when a localized value carries no text in any language. */
function isBlankLocalized(v: LocalizedText | undefined | null): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  return Object.values(v).every((s) => typeof s !== "string" || !s.trim());
}

const EMPTY_SECTION = (order: number): ReportSection => ({
  key: "",
  title: {},
  order,
  render: "narrative",
});

type Draft = {
  name: string;
  nameRu: string;
  nameKz: string;
  isDefault: boolean;
  sections: ReportSection[];
};

const toDraft = (s: ReportStructureDTO | null): Draft => ({
  name: s?.name ?? "",
  nameRu: s?.nameRu ?? "",
  nameKz: s?.nameKz ?? "",
  isDefault: s?.isDefault ?? false,
  sections: s?.sections ?? [EMPTY_SECTION(1)],
});

// ─── Editor ───────────────────────────────────────────────────────
function StructureForm({
  structure,
  onClose,
}: {
  structure: ReportStructureDTO | null; // null = create
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState<Draft>(toDraft(structure));
  const [confirmSectionIdx, setConfirmSectionIdx] = useState<number | null>(null);
  const saveMutation = useSaveReportStructure(structure?.id);

  const updateSection = (index: number, patch: Partial<ReportSection>) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));

  const handleSave = () => {
    // Order follows the on-screen order, so admins reorder by moving rows
    // rather than editing numbers.
    const sections = draft.sections
      .filter((s) => s.key.trim())
      .map((s, i) => ({
        ...s,
        key: s.key.trim(),
        order: i + 1,
        // An empty boilerplate must be null, not "": a blank form text would
        // put the writer into fill-the-form mode with nothing to fill in.
        boilerplate: isBlankLocalized(s.boilerplate) ? null : s.boilerplate,
      }));
    saveMutation.mutate(
      {
        name: draft.name.trim(),
        nameRu: draft.nameRu.trim() || null,
        nameKz: draft.nameKz.trim() || null,
        isDefault: draft.isDefault,
        sections,
      },
      { onSuccess: onClose },
    );
  };

  const canSave =
    draft.name.trim().length > 0 && draft.sections.some((s) => s.key.trim());

  return (
    <div className="border-t px-4 pb-4 pt-3 space-y-4 bg-muted/20">
      <div className="grid grid-cols-3 gap-2">
        {(
          [
            ["name", "EN"],
            ["nameRu", "RU"],
            ["nameKz", "KZ"],
          ] as const
        ).map(([field, label]) => (
          <div key={field} className="space-y-1">
            <Label className="text-xs">
              {t("reportStructures.name")} ({label})
            </Label>
            <Input
              value={draft[field]}
              onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
              className="text-xs"
            />
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={draft.isDefault}
          onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })}
        />
        <span className="text-sm font-medium">
          {t("reportStructures.isDefault")}
        </span>
        <span className="text-xs text-muted-foreground">
          {t("reportStructures.isDefaultHint")}
        </span>
      </label>

      <div className="space-y-3">
        <Label className="text-xs">{t("reportStructures.sections")}</Label>
        {draft.sections.map((section, idx) => (
          <div key={idx} className="rounded-md border p-3 space-y-2 bg-background">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                {idx + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setConfirmSectionIdx(idx)}
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">
                  {t("reportStructures.sectionKey")}
                </Label>
                <Input
                  value={section.key}
                  onChange={(e) => updateSection(idx, { key: e.target.value })}
                  placeholder="complaints"
                  className="text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  {t("reportStructures.sectionRender")}
                </Label>
                <select
                  value={section.render}
                  onChange={(e) =>
                    updateSection(idx, {
                      render: e.target.value as ReportSection["render"],
                    })
                  }
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
                >
                  <option value="narrative">
                    {t("reportStructures.renderNarrative")}
                  </option>
                  <option value="list">{t("reportStructures.renderList")}</option>
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("reportStructures.sectionTitle")}
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {LANGS.map(({ code, label }) => (
                  <div key={code} className="space-y-1">
                    <span className="text-[10px] font-medium text-muted-foreground">
                      {label}
                    </span>
                    <Input
                      value={langValue(section.title, code)}
                      onChange={(e) =>
                        updateSection(idx, {
                          title: setLang(section.title, code, e.target.value),
                        })
                      }
                      className="text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("reportStructures.sectionHint")}
              </Label>
              <Input
                value={langValue(section.hint, i18n.language)}
                onChange={(e) =>
                  updateSection(idx, {
                    hint: setLang(section.hint, i18n.language, e.target.value),
                  })
                }
                className="text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("reportStructures.sectionBoilerplate")}
              </Label>
              <Textarea
                value={langValue(section.boilerplate, i18n.language)}
                onChange={(e) =>
                  updateSection(idx, {
                    boilerplate: setLang(
                      section.boilerplate,
                      i18n.language,
                      e.target.value,
                    ),
                  })
                }
                rows={6}
                className="text-xs font-mono"
              />
              <p className="text-[10px] text-muted-foreground">
                {t("reportStructures.sectionBoilerplateHint")}
              </p>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() =>
            setDraft((d) => ({
              ...d,
              sections: [...d.sections, EMPTY_SECTION(d.sections.length + 1)],
            }))
          }
        >
          <Plus className="h-3 w-3 mr-1" />
          {t("reportStructures.addSection")}
        </Button>
      </div>

      <div className="flex gap-2 justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!canSave || saveMutation.isPending}
        >
          {saveMutation.isPending && (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          )}
          {t("common.save")}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmSectionIdx !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmSectionIdx(null);
        }}
        onConfirm={() => {
          setDraft((d) => ({
            ...d,
            sections: d.sections.filter((_, i) => i !== confirmSectionIdx),
          }));
          setConfirmSectionIdx(null);
        }}
      />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────
export default function ReportStructures() {
  const { t, i18n } = useTranslation();
  const { data: structures = [], isLoading } = useReportStructures();
  const deleteMutation = useDeleteReportStructure();

  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const structureName = (s: ReportStructureDTO) =>
    resolveLocalized(
      { ru: s.nameRu ?? s.name, kz: s.nameKz ?? s.name, en: s.name },
      i18n.language,
    );

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{t("reportStructures.title")}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t("reportStructures.subtitle")}
            </p>
          </div>
          <Button size="sm" onClick={() => setEditingId("new")}>
            <Plus className="h-4 w-4 mr-1" />
            {t("reportStructures.add")}
          </Button>
        </div>

        {editingId === "new" && (
          <Card>
            <StructureForm structure={null} onClose={() => setEditingId(null)} />
          </Card>
        )}

        {isLoading ? (
          <Spinner />
        ) : structures.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("reportStructures.empty")}
          </p>
        ) : (
          structures.map((s) => (
            <Card key={s.id}>
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-4 py-3">
                  <button
                    className="flex items-center gap-2 text-left"
                    onClick={() =>
                      setEditingId(editingId === s.id ? null : s.id)
                    }
                  >
                    {editingId === s.id ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="font-medium text-sm">
                      {structureName(s)}
                    </span>
                    {s.isDefault && (
                      <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary rounded px-1.5 py-0.5">
                        {t("reportStructures.default")}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {t("reportStructures.sectionCount", {
                        count: s.sections.length,
                      })}
                    </span>
                  </button>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() =>
                        setEditingId(editingId === s.id ? null : s.id)
                      }
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      // The default structure is what unlinked templates fall
                      // back to; the API rejects deleting it.
                      disabled={s.isDefault}
                      onClick={() => setConfirmDeleteId(s.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
                {editingId !== s.id && (
                  <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                    {s.sections
                      .slice()
                      .sort((a, b) => a.order - b.order)
                      .map((section) => (
                        <span
                          key={section.key}
                          className="text-xs bg-muted rounded px-2 py-0.5"
                        >
                          {resolveLocalized(section.title, i18n.language) ||
                            section.key}
                        </span>
                      ))}
                  </div>
                )}
                {editingId === s.id && (
                  <StructureForm
                    structure={s}
                    onClose={() => setEditingId(null)}
                  />
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteId(null);
        }}
        onConfirm={() => {
          if (confirmDeleteId) deleteMutation.mutate(confirmDeleteId);
          setConfirmDeleteId(null);
        }}
      />
    </Layout>
  );
}
